import type { IStudioSessionData, IStudioSessionUserInfo } from "@/core/entities/session/StudioSession";
import type { IStorageItem } from "@/core/entities/storage/StorageItem";
import { JsonSerializer } from "@/infrastructure/serializers/JsonSerializer";
import { SessionStorageManager } from "@/infrastructure/services/sessionStorage/SessionStorage.manager";

const STORAGE_KEY = "studioSession";
const SESSION_TTL_SECONDS = 60 * 60 * 24;
const REFRESH_EXPIRY_SKEW_SECONDS = 30;

function isJwt(token: string): boolean {
  return token.split(".").length === 3;
}

function decodeJwtExp(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) {
      return null;
    }

    const padded = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padLength = (4 - (padded.length % 4)) % 4;
    const json = JSON.parse(atob(padded + "=".repeat(padLength))) as { exp?: unknown };

    return typeof json.exp === "number" ? json.exp : null;
  } catch {
    return null;
  }
}

export class SessionRepository {
  private readonly manager = new SessionStorageManager("studioAppUser");
  private readonly serializer = new JsonSerializer();

  save(data: IStudioSessionData): void {
    const item: IStorageItem<IStudioSessionData> = {
      key: STORAGE_KEY,
      value: data,
      timestamp: Date.now(),
      expirationTimeStamp: Date.now() + SESSION_TTL_SECONDS * 1000,
    };
    this.manager.set(STORAGE_KEY, this.serializer.serialize(item));
  }

  clear(): void {
    this.manager.delete(STORAGE_KEY);
  }

  hasSession(): boolean {
    return Boolean(this.getSessionAccessToken() && this.getSessionRefreshToken());
  }

  hasReusableSession(): boolean {
    const accessToken = this.getSessionAccessToken();
    const refreshToken = this.getSessionRefreshToken();
    if (!accessToken || !refreshToken || !isJwt(accessToken) || !isJwt(refreshToken)) {
      return false;
    }

    const refreshExp = decodeJwtExp(refreshToken);
    if (refreshExp === null) {
      return false;
    }

    return refreshExp > Date.now() / 1000 + REFRESH_EXPIRY_SKEW_SECONDS;
  }

  getData(): IStudioSessionData | null {
    const raw = this.manager.get(STORAGE_KEY);
    if (!raw) return null;

    try {
      const item = this.serializer.deserialize<IStorageItem<IStudioSessionData>>(raw);
      if (item.expirationTimeStamp && item.expirationTimeStamp < Date.now()) {
        this.manager.delete(STORAGE_KEY);
        return null;
      }
      return item.value ?? null;
    } catch {
      return null;
    }
  }

  getSessionAccessToken(): string | null {
    return this.getData()?.sessionAccessToken ?? null;
  }

  getSessionRefreshToken(): string | null {
    return this.getData()?.sessionRefreshToken ?? null;
  }

  getUserPermissions(): string[] {
    return this.getData()?.userPermissions ?? [];
  }

  getUserFeatures(): string[] {
    return this.getData()?.userFeatures ?? [];
  }

  getUserRoles(): string[] {
    return this.getData()?.userRoles ?? [];
  }

  getUserInfo(): IStudioSessionUserInfo | null {
    return this.getData()?.userInfo ?? null;
  }

  updateSessionAccessToken(token: string): void {
    const data = this.getData();
    if (!data) return;
    this.save({ ...data, sessionAccessToken: token });
  }
}

export const sessionRepository = new SessionRepository();
